import {
  useState,
  useMemo,
  useEffect
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as amplitude from "@amplitude/analytics-browser";

import {
  DataTable,
  ToggleBox
} from "@unified-checkout/design-system";
import type { IColumnsMap } from "libs/design-system/src/types/data_table";

import { useScreenWidth } from "@app/dashboard/hooks/useScreenWidth";
import { useTestLiveModeStore } from "@app/dashboard/store/testLiveMode";
import { useLangStore } from "@app/dashboard/store/lang";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { PROFILE } from "@app/dashboard/constants/profile";
import { SHARED_EVENTS_PARAMS } from "@app/dashboard/constants/tracking_events/shared";
import {
  PAYMENT_INTEGRATIONS_EVENTS,
  PAYMENT_INTEGRATIONS_EVENTS_PARAMS
} from "@app/dashboard/constants/tracking_events/payment-integrations";
import { MOBILE_BREAKPOINT } from "@app/dashboard/constants/break-points";
import { getCurrentMode } from "@app/dashboard/utils/getCurrentMode";
import { getProfileType } from "@app/dashboard/utils/localStorageUtils";
import { INNER_ROUTES } from "@app/dashboard/constants/inner-routes";

import { getPaymentIntegrations } from "@app/dashboard/services/inner_layout/payment_integrations/get-payment-integrations";
import { useMerchantFlags } from "@app/dashboard/hooks/useMerchantFlags";
import { ToggleModal } from "./components/modals/ToggleModal";
import { Header } from "./Header";
import styles from "./paymentIntegration.module.scss";

const TABLE_ID = 'payment_integrations_table';
const CALLBACKS_ENABLE_RETRIES = 'callbacks_enable_retries';

export const Payment_Integrations = () => {
  const { t } = useTranslation();
  const screenWidth = useScreenWidth();
  const navigate = useNavigate();
  const { lang } = useLangStore();
  const { isLive } = useTestLiveModeStore();

  const isMerchant = getProfileType() === PROFILE.TYPE.MERCHANT;
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const [isError, setIsError] = useState(false);
  const [toggleState, setToggleState] = useState(false)
  const [showToggle, setShowToggle] = useState(false);

  const {
    flagsQuery,
    flagMutation
  } = useMerchantFlags(isMerchant);

  const {
    mutate: flagMutate,
    isPending: flagIsPending,
    isSuccess: flagIsSuccess,
    error: flagError,
    reset: flagReset
  } = flagMutation || {};

  const {
    data: flagsData,
    isLoading
  } = flagsQuery || {};

  const flagData = Boolean(
    flagsData?.find(
      (item: any) => CALLBACKS_ENABLE_RETRIES in item
    )?.callbacks_enable_retries
  );

  useEffect(() => {
    amplitude.track(PAYMENT_INTEGRATIONS_EVENTS.VIEW_PAYMENT_INTAGRATIONS_PAGE, {
      [SHARED_EVENTS_PARAMS.SUBSCRIPTION]: PAYMENT_INTEGRATIONS_EVENTS_PARAMS.PAYMENT_INTEGRATIONS,
      [SHARED_EVENTS_PARAMS.MODE]: getCurrentMode(isLive)
    });
  }, []);

  useEffect(() => {
  if (flagData !== undefined) {
    setToggleState(Boolean(flagData));
  }
}, [flagData]);

  const columnsMap: IColumnsMap = [
    {
      id: 'id',
      name: t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.id'),
      type: 'text',
      key: "id"
    },
    {
      id: 'integration_name',
      name: t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.name'),
      type: 'text',
      key: "integration_name"
    },
    {
      id: 'type',
      name: t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.paymentMethod'),
      type: 'text',
      key: "gateway_type"
    },
    {
      id: 'currency',
      name: t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.currency'),
      type: 'text',
      key: "currency"
    },
    {
      id: 'type',
      name: t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.channel'),
      type: 'text',
      key: "integration_type"
    },
    {
      id: 'shopify',
      name: t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.typeOfIntegration'),
      type: 'text',
      key: {
        conditionsType: 'direct_value',
        conditions: {
          'Shopify': { 'is_shopify': true },
          'Non-Shopify': { 'is_shopify': false }
        },
        default: 'Non-Shopify'
      }
    },
    {
      id: 'date_created',
      name: t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.dateCreated'),
      type: 'date',
      key: "created_at",
      hintText: "created_at",
    }
  ];

  const handleTableRowClick = (rowObj: any) => {
    rowObj && navigate(INNER_ROUTES.SINGLE_PAYMENT_INTEGRATION.replace(':id', rowObj.id));
  };

  const handleToggleChange = (checked: boolean) => {
    flagMutate(
      { [CALLBACKS_ENABLE_RETRIES]: checked },
      {
        onSuccess: (_, variables) => {
          setToggleState(Boolean(variables[CALLBACKS_ENABLE_RETRIES]));
        },
        onError: () => {
          setToggleState((currentValue) => !currentValue);
        },
      }
    );
  };

  const handleToggleBeforeConfirm = async () => {
    if (isLoading) {
      return false;
    }

    setShowToggle(true);
    return false;
  };

  const queryKeys = useMemo(
    () => [QUERY_KEYS.GET_INTEGRATIONS, isLive],
    [isLive]
  );

  const queryFunctionParams = useMemo(
    () => ({
      // ...dateRange,
      is_live: isLive,
      // status: appliedTransactionsStatus
    }),
    [isLive]
  );

  const resetFiltersParams = useMemo(
    () => ([
      isLive
    ]),
    [isLive]
  );

  const toggleFilter = useMemo(() => (
    <div className={styles["paymentIntegration-wrapper__toggle"]}>
      <ToggleBox
        type="transparent box"
        size="md"
        color="default"
        text={t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.toggleLabel')}
        checked={toggleState}
        disabled={isLoading}
        onBeforeToggleConfirmation={handleToggleBeforeConfirm}
      />
    </div>
  ), [toggleState, isLoading, t]);

  const toggleModalProps = {
    isOpen: showToggle,
    flagIsPending,
    setShowToggle,
    flagIsSuccess,
    flagError,
    toggleState,
    flagReset,
    handleToggleChange
  };

  return (
    <div className={styles["paymentIntegration-wrapper"]}>
      {
        // isError ?
        //   <PageStatus
        //     status="error"
        //     title={t('innerLayout.errors.smthWentWrong')}
        //     description={t('innerLayout.errors.dataRetrievalErr')}
        //     icon={CircleExclamation}
        //   />
        //   :
        <>
          <Header />

          <DataTable
            lang={lang}
            {...isMerchant && { additionalFilter: toggleFilter }}
            tableBody={{
              lang: lang,
              tableId: TABLE_ID,
              dataLocation: "data.data.results",
              columnsMap: columnsMap,
              rowClickHandler: handleTableRowClick
            }}

            pagination={{
              hideLabels: isMobile,
              type: "page_based",
              dataLocation: {
                totalItems: "data.data.count"
              },
              isMobile: isMobile,
              // maxVisiblePages: 5,
              // compactMode: isMobile
            }}

            queries={{
              queryKeys: queryKeys,
              queryFunction: getPaymentIntegrations,
              queryFunctionParams: queryFunctionParams,
              resetFiltersParams: resetFiltersParams
            }}

            errorState={{
              onError: (error) => setIsError(error)
            }}

            emptyState={{
              text: t('innerLayout.transactionsPage.table.body.noTransactionsYet'),
              description: t('innerLayout.transactionsPage.table.body.noTransactionsHint')
            }}
          />
        </>
      }
      <div className={styles["paymentIntegration-wrapper__toggle-modal"]}>
        <ToggleModal {...toggleModalProps} />
      </div>
    </div>
  )
}